import React, { useState } from "react";
import { Mail, MapPin, ShieldCheck, CheckCircle2, CalendarClock } from "lucide-react";
import { Eyebrow } from "@/components/Bits";
import { BACKEND_URL, CALENDLY_URL, CONTACT_EMAIL, LOCATION } from "@/lib/site";

const inquiryOptions = [
  { value: "owner", label: "I own a business (Sell-Side)" },
  { value: "buyer", label: "I am evaluating a target (Buy-Side)" },
  { value: "advisor", label: "I advise business owners" },
  { value: "general", label: "General inquiry" },
];

const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", organization: "", phone: "", inquiry_type: "owner", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setSending(true);
    setError("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      setSubmitted(true);
      setForm({ name: "", email: "", organization: "", phone: "", inquiry_type: "owner", message: "" });
    } catch (err) {
      setError("Something went wrong. Please email us directly at " + CONTACT_EMAIL + ".");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-[#E2E8F0] text-ink px-4 py-3 text-base placeholder:text-[#94A3B8] focus:outline-none focus:border-brand transition-colors";

  return (
    <main data-testid="contact-page" className="pt-20">
      {/* HERO */}
      <section className="bg-cream py-20 md:py-24 relative">
        <div className="grid-lines" />
        <div className="max-w-7xl mx-auto px-5 md:px-10 relative z-10">
          <div className="max-w-3xl">
            <Eyebrow>Book a Strategy Call</Eyebrow>
            <h1 className="font-serif text-4xl md:text-6xl leading-[1.08] text-ink-deep">
              Let’s start a <span className="italic text-brand">conversation.</span>
            </h1>
            <p className="mt-7 text-xl text-[#475569]">
              Whether you’re preparing a founder-led business for its next chapter, evaluating a target, or advising
              a client through a transition — book a confidential call or send a note below.
            </p>
          </div>
        </div>
      </section>

      {/* CALENDAR + FORM */}
      <section className="bg-white py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          {/* Calendar */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <CalendarClock className="w-6 h-6 text-brand" />
              <h2 className="font-serif text-2xl text-ink-deep">Schedule directly</h2>
            </div>
            <div className="border border-[#E2E8F0] overflow-hidden bg-white">
              <iframe
                title="Schedule with Cultural Capital"
                data-testid="calendly-embed"
                src={`${CALENDLY_URL}?hide_gdpr_banner=1&background_color=ffffff&text_color=1e293b&primary_color=1c86c4`}
                className="w-full"
                style={{ height: "680px", border: "0" }}
              />
            </div>
            <p className="text-xs text-[#94A3B8] mt-3">
              Booking powered by Calendly. If the calendar doesn’t load, email us at
              {" "}<a className="text-brand underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
            </p>
          </div>

          {/* Form */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <Mail className="w-6 h-6 text-brand" />
              <h2 className="font-serif text-2xl text-ink-deep">Or send a message</h2>
            </div>
            <div className="bg-cream border border-[#E2E8F0] p-7">
              {submitted ? (
                <div data-testid="contact-success" className="py-10 text-center">
                  <CheckCircle2 className="w-12 h-12 text-brand mx-auto mb-4" />
                  <h3 className="font-serif text-2xl text-ink-deep mb-2">Thank you.</h3>
                  <p className="text-[#475569]">We’ve received your note and will be in touch shortly to schedule a confidential conversation.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                  <div>
                    <label className="block text-sm text-[#475569] mb-1.5">Name *</label>
                    <input name="name" value={form.name} onChange={handleChange} required className={inputCls} data-testid="contact-name" />
                  </div>
                  <div>
                    <label className="block text-sm text-[#475569] mb-1.5">Email *</label>
                    <input type="email" name="email" value={form.email} onChange={handleChange} required className={inputCls} data-testid="contact-email" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-[#475569] mb-1.5">Organization</label>
                      <input name="organization" value={form.organization} onChange={handleChange} className={inputCls} data-testid="contact-org" />
                    </div>
                    <div>
                      <label className="block text-sm text-[#475569] mb-1.5">Phone</label>
                      <input name="phone" value={form.phone} onChange={handleChange} className={inputCls} data-testid="contact-phone" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-[#475569] mb-1.5">I am a…</label>
                    <select name="inquiry_type" value={form.inquiry_type} onChange={handleChange} className={inputCls} data-testid="contact-inquiry">
                      {inquiryOptions.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-[#475569] mb-1.5">Message</label>
                    <textarea name="message" value={form.message} onChange={handleChange} rows={4} className={`${inputCls} resize-none`} data-testid="contact-message" />
                  </div>
                  {error && <p className="text-sm text-red-600" data-testid="contact-error">{error}</p>}
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-submit"
                    className="w-full bg-ink text-white px-7 py-3.5 text-sm font-medium tracking-wide hover:bg-ink-deep transition-colors disabled:opacity-60"
                  >
                    {sending ? "Sending…" : "Send Message"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT INFO STRIP */}
      <section className="bg-cream py-16">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid sm:grid-cols-3 gap-6">
          <div className="bg-white border border-[#E2E8F0] p-7">
            <Mail className="w-6 h-6 text-brand mb-4" />
            <p className="text-xs uppercase tracking-[0.18em] text-[#64748B] mb-2">Email</p>
            <a href={`mailto:${CONTACT_EMAIL}`} className="font-serif text-lg text-ink-deep hover:text-brand transition-colors">{CONTACT_EMAIL}</a>
          </div>
          <div className="bg-white border border-[#E2E8F0] p-7">
            <MapPin className="w-6 h-6 text-brand mb-4" />
            <p className="text-xs uppercase tracking-[0.18em] text-[#64748B] mb-2">Location</p>
            <p className="font-serif text-lg text-ink-deep">{LOCATION}</p>
            <p className="text-sm text-[#64748B] mt-1">Serving Toronto, the GTA &amp; Southwestern Ontario</p>
          </div>
          <div className="bg-white border border-[#E2E8F0] p-7">
            <ShieldCheck className="w-6 h-6 text-brand mb-4" />
            <p className="text-xs uppercase tracking-[0.18em] text-[#64748B] mb-2">Our Promise</p>
            <p className="font-serif text-lg text-ink-deep">Fixed-Fee. Conflict-Free. Confidential.</p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
